import { createContact } from '@/hubspot.js';
import { Utils } from '@/utils.js';
import { apiFetch } from '@/utils/api.js';

export const Report = {
    async generateReport(store) {
        // Start timing the report generation
        const startTime = performance.now();
        $log.log(`[report:generateReport] Started at ${new Date().toISOString()}`);

        store.loading_message = 'Your report is generating. Please do not close or reload this tab.';
        store.overlays.loading = true;

        /*
		------------------------------------------------------------------------
		HubSopot contact creation so we can be sure we have the user id and
        company id to work with.
		------------------------------------------------------------------------
		*/

        // Create the HubSpot contact.
        let createContactResponse = true;
        createContactResponse = await createContact(store, true);

        // Only continue if we get a positive response from the contact creation.
        if (createContactResponse) {
            /*
			------------------------------------------------------------------------
			HubSpot create deal
			------------------------------------------------------------------------
			*/
            // FAILSAFE: Always create deals in production, regardless of flag
            const shouldCreateDeal = wpData.isProd || wpData.createHubspotDeals;

            let dealResult;
            if (shouldCreateDeal) {
                const dealPayload = {
                    DealName: store.user.projectname + ' - ' + store.user.roomname,
                    DealDescription: '',
                    ContactId: store.user.user_id,
                };
                if (import.meta.env.MODE !== 'production') {
                    dealPayload.DealName = '(TEST ONLY - DELETE) ' + dealPayload.DealName;
                }
                const response = await apiFetch('/wp-json/lat/v1/hubspot-create-deal', {
                    method: 'POST',
                    body: JSON.stringify(dealPayload),
                });
                dealResult = await response.json();
                if (response.ok) {
                    $log.log(`[report:generateReport] Deal was created.`);
                    $log.log(`[report:generateReport] dealResult: ${JSON.stringify(dealResult, null, 4)}`);
                } else {
                    store.overlays.loading = false;
                    store.resetLoadingMessage();
                    store.overlays.reporterror.open = true;
                    return false;
                }
            } else {
                $log.log('[report:generateReport] HubSpot deal creation skipped for DEV environment. See constant "LX_CREATE_HUBSPOT_DEALS".');
                // Mock successful response for development
                dealResult = {
                    status: 200,
                    message: 'Deal creation skipped (DEV mode)',
                    data: [{ 'Hubspot ID': 'DEV-MOCK-ID' }],
                };
            }

            /*
			------------------------------------------------------------------------
			Generate report document in Google Drive
			------------------------------------------------------------------------
			*/
            // Calculate lumen table data
            const lumenTableData = store.calculateLumenTableData;

            // Get selected lumen output type and value from primary product
            const primaryProduct = store.getPrimaryProduct;
            let selectedLumenOutputType = '';
            let selectedLumenOutputValue = 0;
            let selectedLumenOutputValueTotal = 0;

            if (primaryProduct && primaryProduct.selectedLumenOutput && primaryProduct.dataExtended) {
                selectedLumenOutputType = primaryProduct.selectedLumenOutput;
                // Find the matching extended data entry
                const selectedOutputData = primaryProduct.dataExtended.find((item) => item.output === primaryProduct.selectedLumenOutput);
                if (selectedOutputData) {
                    selectedLumenOutputValue = selectedOutputData.lumen_total || 0;

                    // Calculate total: lumen value per unit × number of units
                    const requiredUnits =
                        primaryProduct.calculation?.unitsUsed || primaryProduct.manualUnits || primaryProduct.calculation?.calculatedUnits || 0;
                    selectedLumenOutputValueTotal = selectedLumenOutputValue * requiredUnits;
                }
            }

            const documentPayload = {
                hubspotContactId: store.user.user_id,
                firstName: store.user.firstname,
                lastName: store.user.lastname,
                email: store.user.email,
                projectName: store.user.projectname,
                roomName: store.user.roomname,
                companyName: store.user.company,
                roomUnit: store.user.room.unit,
                roomUsage: store.user.room.usage,
                roomWidth: store.user.room.width,
                roomLength: store.user.room.length,
                roomHeight: store.user.room.height,
                roomMaterials: store.user.room.materials && store.user.room.materials.length > 0
                    ? store.user.room.materials
                        .map((item) =>
                            item
                                .split(/[_\s]/)
                                .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                                .join(' '),
                        )
                        .join(', ')
                    : '',
                // Room baseline (without products)
                startReverbTime: store.roomBaseline?.roomT60,
                startingRoomSabins: store.roomBaseline?.roomMetricSabins,
                // Combined calculation (all products)
                recommendedRoomSabins: store.combinedCalculation?.requiredTotalMetricSabins,
                targetReverbTime: store.user.room.usagetarget,
                // Multi-product structure
                products: store.user.products.map((product) => {
                    // Resolve size and variant data from product catalog (same as currentProduct getter)
                    const sizeData = product.data?.sizes?.items?.[product.size];
                    const variantData = product.size_variant ? product.data?.sizes?.variants?.all?.[product.size_variant] : null;

                    return {
                        id: product.id,
                        productName: product.data?.name || '',
                        productDescription: product.data?.desc || '',
                        productCode: product.size_variant || sizeData?.id || '',
                        size: product.size,
                        sizeVariant: product.size_variant || '',
                        // Resolved size properties
                        sizeId: sizeData?.id || '',
                        sizeWidth: sizeData?.w || 0,
                        sizeDepth: sizeData?.d || 0,
                        sizeHeight: variantData?.h || sizeData?.h || 0,
                        metricSabins: variantData?.metric_sabins || sizeData?.metric_sabins || '',
                        // Resolved image paths
                        heroImage: Utils.resolveHeroImage(product.data?.images?.hero, product.shape),
                        coverImage: product.data?.images?.cover?.[0] || '',
                        specSheetUrl: product.data?.spec_sheet_permalink || '',
                        // Product data
                        extendedData: product.dataExtended || [],
                        selectedLumenOutput: product.selectedLumenOutput || '',
                        calculation: product.calculation,
                        manualUnits: product.manualUnits,
                    };
                }),
                activeProductId: store.user.activeProductId,
                // Room baseline and combined calculations (from batch API)
                roomBaseline: store.roomBaseline,
                combinedCalculation: store.combinedCalculation,
                // Room mockup image - send base64 data directly for DOCX (no Google Drive round trip)
                // Fallback to Google Drive file ID for backward compatibility with Google Docs API
                roomMockupImage: store.user.roomMockupImageBase64 || null,
                productImageUploadId: store.user.imgUploadId, // Backward compatibility
                lumenTableData: lumenTableData,
                // Selected lumen output data
                selectedLumenOutputType: selectedLumenOutputType,
                selectedLumenOutputValue: selectedLumenOutputValue,
                selectedLumenOutputValueTotal: selectedLumenOutputValueTotal,
                contact: createContactResponse,
                deal: dealResult.data,
            };

            $log.log(`[report:generateReport] documentPayload: ${JSON.stringify(documentPayload, null, 4)}`);

            /*
			------------------------------------------------------------------------
			Generate document in Google Drive
			------------------------------------------------------------------------
			*/
            const documentResponse = await apiFetch('/wp-json/lat/v1/generate-document', {
                method: 'POST',
                body: JSON.stringify(documentPayload),
            });
            const documentResult = await documentResponse.json();
            if (documentResponse.ok) {
                // Log timing for successful document generation
                const endTime = performance.now();
                const elapsedSeconds = ((endTime - startTime) / 1000).toFixed(2);
                $log.log(`[report:generateReport] Document was created.`);
                $log.log(`[report:generateReport] REPORT GENERATION COMPLETED - Total time: ${elapsedSeconds} seconds`);
                $log.log(`[report:generateReport] documentResult: ${JSON.stringify(documentResult, null, 4)}`);
            } else {
                // Log timing for failed document generation
                const endTime = performance.now();
                const elapsedSeconds = ((endTime - startTime) / 1000).toFixed(2);
                $log.log(`[report:generateReport] REPORT GENERATION FAILED - Total time before failure: ${elapsedSeconds} seconds`);

                store.overlays.loading = false;
                store.resetLoadingMessage();
                store.overlays.reporterror.message =
                    'Oops! We encountered an unexpected issue while generating your report. Please click/tap OK to try again. Thank you for your patience!';
                store.overlays.reporterror.open = true;
                $log.log(`[report:generateReport] documentResponse: ${JSON.stringify(documentResponse, null, 4)}`);
                return false;
            }

            // Attach document to deal? (HS)

            // Email document to Luxxbox along with attachment, details and deal url

            // Email document to customer

            // Insert data row into Google Sheet

            return setTimeout(() => {
                store.overlays.loading = false;
                store.resetLoadingMessage();
                store.user.reportGenerated = true;
                window.scrollTo({
                    top: 0,
                    // left: 0,
                    behavior: 'smooth',
                });
                store.setStepStatus('Report', 'COMPLETE');
                return true;
            }, 0);
        }
    },

    /*
    ---------------------------------------------------------------------------
    Call API endpoint to save the SVG to disk.
    ---------------------------------------------------------------------------
    */
    async saveImage(store) {
        $log.log(`[report:saveImage] Starting image save`);
        store.overlays.loading = true;
        const svgImageValue = store.svgTemp;
        if (svgImageValue) {
            const svgData = new XMLSerializer().serializeToString(svgImageValue);
            try {
                const response = await apiFetch('/wp-json/lat/v1/save-image', {
                    method: 'POST',
                    body: JSON.stringify({
                        svg_data: svgData,
                    }),
                });
                const result = await response.json();
                if (response.ok) {
                    store.productImageUploadId = result.file_id;
                    $log.log(`[report:saveImage] SUCCESS`);
                } else {
                    $log.log(`[report:saveImage] ERROR`);
                }
            } catch (error) {
                $log.log(`[report:saveImage] ERROR`);
            }
        } else {
            $log.log(`[report:saveImage] ERROR`);
        }
        store.overlays.loading = false;
        $log.log(`[report:saveImage] Complete`);
    },
};
